import React, { useState, useMemo, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import {
  useCreateDataChangeRequestMutation,
  useEmployeeRecordFieldsQuery,
  RequestFormFragment,
  RequestForm_EmployeeFragment,
  ObjectFieldValueFragment,
} from '../../../__generated__/graphql';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { useToasts } from '../../../../../context/Toast';
import { useUserInfo } from '../../../context/UserInfo';
import { StepSelectRequestForm } from './StepSelectRequestForm';
import { StepSelectEmployee } from './StepSelectEmployee';
import { StepReasonFiles } from './StepReasonFiles';
import { employeeObjectApiName, RequestFormData, StepType } from './types';
import { NavigationButtons } from './NavigationButtons';
import { PositionChangeComponent } from './components/positionChange';
import { SalaryChangeStepsWrapper } from './components/salaryChange';
import { StepEmployeeDetailsComponent } from './components/employeeDetails/StepEmployeeDetailsComponent';
import { Stack, Spinner } from 'react-bootstrap';
import { mapToCreateDataChangeRequestInput } from './mappings';
import {
  getAllSteps,
  getEmployeeDefaultValues,
  getEmployeeSalary,
  isStepValid,
  mapToRecordField,
  validateOnSubmit,
} from './logic';
import { StepReview } from './StepReview';

type Props = {
  show: boolean;
  showAllRequests: boolean;
  requestForms: readonly RequestFormFragment[];
  employees: readonly RequestForm_EmployeeFragment[];
  onHide: () => void;
};

export const RequestForm: React.FC<Props> = ({
  show,
  requestForms,
  employees,
  onHide,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const { addSuccess, addError } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const { id: currentUserId } = useUserInfo();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<RequestFormData>({});

  const [createDataChangeRequest, { loading: isSubmittingRequest }] =
    useCreateDataChangeRequestMutation();

  const selectedRequestForm = useMemo(() => {
    return requestForms.find((form) => form.Id === formData.requestFormId);
  }, [requestForms, formData.requestFormId]);

  const employeeObjectFields = useMemo(() => {
    return (
      selectedRequestForm?.objectsFields.find(
        (field) => field.objectName === employeeObjectApiName,
      )?.fields || []
    );
  }, [selectedRequestForm]);

  const {
    data: employeeRecordFieldsData,
    loading: employeeRecordFieldsLoading,
  } = useEmployeeRecordFieldsQuery({
    variables: {
      employeeId: formData.employeeId || '',
      recordType: employeeObjectApiName,
      fields: employeeObjectFields,
    },
    skip: !formData.requestFormId || !formData.employeeId,
  });

  const employeeRecordFields: readonly ObjectFieldValueFragment[] =
    useMemo(() => {
      return employeeRecordFieldsData?.recordFields || [];
    }, [employeeRecordFieldsData]);

  const selectedEmployee = employees.find(
    (emp) => emp.Id === formData.employeeId,
  );

  const handleEmployeeSelect = (employeeId: string | null) => {
    setFormData((prev) => ({
      ...prev,
      employeeId: employeeId,
      reason: '',
      files: [],
      fields: {},
    }));
    if (employeeId?.length) {
      setCurrentStep((step) => step + 1);
    }
  };

  const availableSteps = useMemo(() => {
    return getAllSteps(selectedRequestForm);
  }, [selectedRequestForm]);

  const handleStepBack = () => {
    if (currentStep === 0) {
      handleHide();
      return;
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleStepNext = () => {
    if (currentStep === availableSteps.length - 1) {
      handleFinalSubmit();
      return;
    }
    setCurrentStep((step) => step + 1);
  };

  const handleFinalSubmit = async () => {
    const error = validateOnSubmit(availableSteps, formData);
    if (error) {
      addError(error);
      return;
    }

    const input = await mapToCreateDataChangeRequestInput(
      currentUserId,
      employeeRecordFields,
      formData,
    );
    if (!input) {
      return;
    }

    try {
      await createDataChangeRequest({
        variables: {
          input,
        },
      });
      addSuccess(t('notifications.success'));
      setCurrentStep(0);
      setFormData({});
      onHide();
    } catch (error) {
      errorHandler(error);
    }
  };

  const currentStepType = availableSteps[currentStep];

  const isCurrentStepValid = useMemo(() => {
    return isStepValid(currentStepType, selectedRequestForm, formData);
  }, [currentStepType, selectedRequestForm, formData]);

  const handleEmployeeDetailsChange = useCallback(
    (fieldData: { [key: string]: string }) => {
      setFormData((prev) => ({ ...prev, fields: fieldData }));
    },
    [],
  );

  const currentSalary = useMemo(() => {
    return getEmployeeSalary(employeeRecordFields);
  }, [employeeRecordFields]);

  const defaultValues = useMemo(() => {
    return getEmployeeDefaultValues(employeeRecordFields, formData.fields);
  }, [employeeRecordFields, formData.fields]);

  const memoizedPositionData = useMemo(() => {
    return formData.positionChange
      ? {
          effectiveDate: formData.positionChange.effectiveDate,
          unknownEndDate: formData.positionChange.unknownEndDate,
          endDate: formData.positionChange.endDate,
        }
      : undefined;
  }, [formData.positionChange]);

  const renderStep = () => {
    const recordFields = employeeRecordFields.map(mapToRecordField);

    if (currentStepType === StepType.SelectForm) {
      return (
        <StepSelectRequestForm
          requestForms={requestForms}
          onSubmit={handleStepNext}
          onCancel={onHide}
          selectedFormId={formData.requestFormId ?? null}
          onFormSelect={(formId) =>
            setFormData((prev) => ({ ...prev, requestFormId: formId }))
          }
        />
      );
    }

    if (currentStepType === StepType.SelectEmployee) {
      return (
        <StepSelectEmployee
          employees={employees}
          selectedEmployeeId={formData.employeeId ?? null}
          onEmployeeSelect={handleEmployeeSelect}
        />
      );
    }

    if (currentStepType === StepType.ReasonFiles) {
      if (!formData.employeeId || !selectedRequestForm) {
        return null;
      }
      const isReasonMandatory =
        selectedRequestForm?.flair__Mandatory_Reason__c || false;
      return (
        <StepReasonFiles
          reason={formData.reason || ''}
          setReason={(newReason) =>
            setFormData((prev) => ({ ...prev, reason: newReason }))
          }
          selectedFiles={formData.files || []}
          setSelectedFiles={(newFiles) =>
            setFormData((prev) => ({ ...prev, files: newFiles }))
          }
          isReasonMandatory={isReasonMandatory}
        />
      );
    }

    if (currentStepType === StepType.Position) {
      return (
        <Stack gap={0}>
          <div className="mb-4">
            <h4>{t('steps.positionChange')}</h4>
            <p className="text-muted">
              {t('steps.position_changeDescription')}
            </p>
          </div>
          <PositionChangeComponent
            selectedEmployeeId={formData.employeeId!}
            onChange={(data) => {
              return setFormData((prev) => ({ ...prev, positionChange: data }));
            }}
            value={formData.positionChange}
          />
        </Stack>
      );
    }

    if (currentStepType === StepType.Salary) {
      if (!formData.employeeId) {
        throw new Error('Employee ID is required for salary change');
      }
      return (
        <Stack gap={0}>
          <div className="mb-4">
            <h4>{t('steps.salaryChange')}</h4>
            <p className="text-muted">{t('steps.salary_changeDescription')}</p>
          </div>
          <SalaryChangeStepsWrapper
            selectedEmployeeId={formData.employeeId}
            currentSalary={currentSalary}
            currency="€"
            onChange={(data) =>
              setFormData((prev) => ({ ...prev, salaryChange: data }))
            }
            value={formData.salaryChange}
            positionChangeData={memoizedPositionData}
          />
        </Stack>
      );
    }

    if (currentStepType === StepType.EmployeeDetails) {
      return (
        <StepEmployeeDetailsComponent
          recordFields={recordFields}
          defaultValues={defaultValues}
          onChange={handleEmployeeDetailsChange}
        />
      );
    }

    if (currentStepType === StepType.Review) {
      const selectedForm = requestForms.find(
        (form) => form.Id === formData.requestFormId,
      );
      return (
        <StepReview
          formData={formData}
          selectedRequestForm={selectedForm || null}
          recordFields={recordFields}
          selectedEmployee={selectedEmployee}
        />
      );
    }

    return null;
  };

  const handleHide = () => {
    setCurrentStep(0);
    setFormData({});
    onHide();
  };

  const handleRemoveEmployee = () => {
    setFormData((form) => ({
      requestFormId: form.requestFormId,
    }));
    setCurrentStep(0);
  };

  const selectedEmployeeName = selectedEmployee?.Name;
  const isLoading = employeeRecordFieldsLoading || isSubmittingRequest;

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton={!isSubmittingRequest}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <Modal.Title className="fs-2">
              {currentStepType === StepType.SelectForm
                ? t('title')
                : selectedRequestForm?.Name || t('title')}
            </Modal.Title>
            <div className="text-muted fs-5 mt-1">
              {getStepDescription(currentStepType, t)}
            </div>
          </div>
          {currentStep > 0 && selectedEmployeeName && (
            <div className="me-1">
              <span className="badge bg-primary d-flex align-items-center gap-3 fs-5 py-3 px-4">
                {selectedEmployeeName}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  style={{ fontSize: '1rem' }}
                  onClick={handleRemoveEmployee}
                  disabled={isLoading}
                />
              </span>
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {isLoading && show && <Loading />}
        {renderStep()}
        <NavigationButtons
          onBack={handleStepBack}
          onNext={handleStepNext}
          disableNext={!isCurrentStepValid || isLoading}
          disableBack={isLoading}
          currentStep={currentStep}
          totalSteps={availableSteps.length}
        />
      </Modal.Body>
    </Modal>
  );
};

function getStepDescription(
  stepType: StepType,
  t: (key: string) => string,
): string {
  if (stepType === StepType.SelectForm) return t('steps.stepOnePlaceholder');
  if (stepType === StepType.SelectEmployee)
    return t('steps.stepTwoPlaceholder');
  if (stepType === StepType.ReasonFiles) return t('steps.reasonFiles');
  if (stepType === StepType.Position) return t('steps.positionChange');
  if (stepType === StepType.Salary) return t('steps.salaryChange');
  if (stepType === StepType.EmployeeDetails) return t('steps.employeeDetails');
  if (stepType === StepType.Review) return t('steps.stepThreePlaceholder');
  return '';
}

const Loading: React.FC = () => {
  return (
    <Stack gap={0}>
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" />
      </div>
    </Stack>
  );
};
